import { produce } from 'immer';

import { Point2D, TrussEnvelope, TrussEnvelopeBevelCut } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { InsertingTrussEnvelopeData } from '../../../../store/slices/applicationSlice';
import { StageOneTrussEnvelope } from './stage1';
import { StageTwoTrussEnvelope } from './stage2';
import { StageThreeTrussEnvelope } from './stage3';

export type EnvelopeRecalculationEnd = 'left' | 'right' | 'both';

type CalculableEnvelope = Pick<
  TrussEnvelope,
  | 'leftPoint'
  | 'rightPoint'
  | 'elevation'
  | 'leftBevelCut'
  | 'rightBevelCut'
  | 'leftOverhang'
  | 'rightOverhang'
  | 'containerLevel'
  | 'capLevelOne'
  | 'capLevelTwo'
>;

export interface TrussEnvelopeHistory {
  stageOne: StageOneTrussEnvelope;
  stageTwo: StageTwoTrussEnvelope;
  stageThree: StageThreeTrussEnvelope;
}

export class EnvelopeCalculationResult {
  leftPoint: Point2D;
  rightPoint: Point2D;

  elevation: number;

  leftBevelCut: TrussEnvelopeBevelCut | null;
  rightBevelCut: TrussEnvelopeBevelCut | null;

  leftOverhang: number;
  rightOverhang: number;

  history?: TrussEnvelopeHistory;

  constructor(
    leftPoint: Point2D,
    rightPoint: Point2D,
    elevation: number,
    leftBevelCut: TrussEnvelopeBevelCut | null,
    rightBevelCut: TrussEnvelopeBevelCut | null,
    leftOverhang: number,
    rightOverhang: number,
    options?: { history?: TrussEnvelopeHistory },
  ) {
    this.leftPoint = leftPoint;
    this.rightPoint = rightPoint;
    this.elevation = elevation;
    this.leftBevelCut = leftBevelCut;
    this.rightBevelCut = rightBevelCut;
    this.leftOverhang = leftOverhang;
    this.rightOverhang = rightOverhang;

    if (options?.history) {
      this.history = options.history;
    }
  }

  static fromDistinctPoints(
    leftPoint: Point2D,
    rightPoint: Point2D,
    elevation: number,
    leftOverhang: number,
    rightOverhang: number,
  ) {
    return new EnvelopeCalculationResult(leftPoint, rightPoint, elevation, null, null, leftOverhang, rightOverhang);
  }

  applyTo<T extends CalculableEnvelope>(envelope: T, end: EnvelopeRecalculationEnd = 'both') {
    return produce(envelope, (draft) => {
      switch (envelope.containerLevel) {
        case 'NotCapped':
        case 'BaseLevel':
          draft.elevation = this.elevation;
          break;
        case 'CapLevelOne':
          draft.elevation = draft.capLevelOne;
          break;
        case 'CapLevelTwo':
          draft.elevation = draft.capLevelTwo;
          break;
        default:
          assertExhaustiveSwitch(envelope.containerLevel);
      }

      if (end === 'left' || end === 'both') {
        draft.leftPoint = this.leftPoint;
        draft.leftBevelCut = this.leftBevelCut;
        draft.leftOverhang = this.leftOverhang;
      }

      if (end === 'right' || end === 'both') {
        draft.rightPoint = this.rightPoint;
        draft.rightBevelCut = this.rightBevelCut;
        draft.rightOverhang = this.rightOverhang;
      }
    });
  }

  toTrussEnvelope(
    name: string,
    data: InsertingTrussEnvelopeData & { roofContainerGuid: string | null },
  ): TrussEnvelope {
    return {
      ...data,

      name,

      leftPoint: this.leftPoint,
      rightPoint: this.rightPoint,
      elevation: this.elevation,
      leftBevelCut: this.leftBevelCut,
      rightBevelCut: this.rightBevelCut,
      leftOverhang: this.leftOverhang,
      rightOverhang: this.rightOverhang,
    };
  }
}
